import { apiFetch } from './api.js';
import {
  DB, session, sessionMode, currentView, settingsDirty, currentParentId, currentPerformerId, currentHelpSlug, viewingAsAdmin, COACHING_MINE,
  setCurrentView, setSettingsDirty, setCurrentParentId, setCurrentPerformerId, setCurrentHelpSlug,
} from './state.js';
import { canEdit, isDual, isAdmin, isPcFamily } from './domain-utils.js';

// ── NAV / ROUTING ────────────────────────────────────────────────────
export const ICONS = {
  // Dashboard — speedometer / gauge
  dashboard: `<svg aria-hidden="true" viewBox="0 0 16 16"><path d="M2 10a6 6 0 1 1 12 0"/><path d="M8 4v1M4.2 5.2l.7.7M11.8 5.2l-.7.7M2 10h1M13 10h1"/><path d="M8 10l2.5-3"/></svg>`,
  // Family / parents — two people
  family: `<svg aria-hidden="true" viewBox="0 0 16 16"><circle cx="5.5" cy="4.5" r="2"/><path d="M1 13c0-2.5 2-4 4.5-4s4.5 1.5 4.5 4"/><circle cx="12" cy="5" r="1.5"/><path d="M10 13c0-1.8 1-3 2-3s2 1.2 2 3"/></svg>`,
  // Performers / star person
  performers: `<svg aria-hidden="true" viewBox="0 0 16 16"><circle cx="8" cy="4.5" r="2"/><path d="M4 13c0-2.2 1.8-4 4-4s4 1.8 4 4"/><path d="M8 7.5l.6 1.8H11l-1.5 1.1.6 1.8L8 11.1l-1.5 1.1.6-1.8L5.5 9.3h1.9z" stroke-width="1"/></svg>`,
  // Billing — credit card
  billing: `<svg aria-hidden="true" viewBox="0 0 16 16"><rect x="1" y="3.5" width="14" height="9" rx="1.5"/><path d="M1 6.5h14"/><path d="M4 9.5h2M4 11h1"/></svg>`,
  registrations: `<svg aria-hidden="true" viewBox="0 0 16 16"><rect x="3" y="2.5" width="10" height="12" rx="1.5"/><rect x="5.5" y="1.5" width="5" height="2.5" rx="1"/><path d="M5.5 7h5M5.5 9.5h5M5.5 12h3"/></svg>`,
  // Users — person with badge
  users: `<svg aria-hidden="true" viewBox="0 0 16 16"><circle cx="7" cy="5" r="2.5"/><path d="M2 13c0-2.8 2.2-5 5-5"/><rect x="9.5" y="9.5" width="5" height="5" rx="1"/><path d="M10.5 12h3M12 10.5v3"/></svg>`,
  // Settings — sliders
  settings: `<svg aria-hidden="true" viewBox="0 0 16 16"><path d="M2 4h12M2 8h12M2 12h12"/><circle cx="5" cy="4" r="1.5" fill="var(--bg-card)"/><circle cx="10" cy="8" r="1.5" fill="var(--bg-card)"/><circle cx="6" cy="12" r="1.5" fill="var(--bg-card)"/></svg>`,
  // My Family — house with heart
  myfamily: `<svg aria-hidden="true" viewBox="0 0 16 16"><path d="M2 8L8 2l6 6"/><path d="M4 8v5.5h3v-3h2v3h3V8"/><path d="M7 6.5c0-.8.5-1.5 1-1.5s1 .7 1 1.5c0 1-1 2-1 2s-1-1-1-2z" stroke-width="1"/></svg>`,
  // Billing & Payments (parent) — wallet
  // Classes, Coaching and Summer Workshops shared one ticket glyph until now,
  // which made three separate sections indistinguishable in the sidebar. They
  // can't be told apart thematically -- Classes is Tap & Voice and Coaching is
  // audition/voice work, so any music glyph fits both -- so they're split by
  // what each structurally is: Classes is music, Coaching is scheduled time
  // (20-minute slots), Summer Workshops is the season.
  // Classes — musical note
  classes: `<svg aria-hidden="true" viewBox="0 0 16 16"><path d="M6.5 12V3.2l6-1.4v8.8"/><ellipse cx="4.6" cy="12.2" rx="1.9" ry="1.5"/><ellipse cx="10.6" cy="10.6" rx="1.9" ry="1.5"/></svg>`,
  // Coaching — stopwatch
  coaching: `<svg aria-hidden="true" viewBox="0 0 16 16"><circle cx="8" cy="9.3" r="5"/><path d="M8 9.3V6.6"/><path d="M6.3 1.6h3.4"/><path d="M8 1.6v1.8"/><path d="M12.4 5.2l1-1"/></svg>`,
  // Summer Workshops — sun
  summer: `<svg aria-hidden="true" viewBox="0 0 16 16"><circle cx="8" cy="8" r="3.1"/><path d="M8 1.2v1.6M8 13.2v1.6M1.2 8h1.6M13.2 8h1.6M3.3 3.3l1.1 1.1M11.6 11.6l1.1 1.1M12.7 3.3l-1.1 1.1M4.4 11.6l-1.1 1.1"/></svg>`,
  wallet: `<svg aria-hidden="true" viewBox="0 0 16 16"><rect x="1" y="4" width="14" height="10" rx="1.5"/><path d="M1 7h14"/><path d="M4 1.5l10 2.5"/><circle cx="11.5" cy="10" r="1" fill="var(--gold)" stroke="none"/></svg>`,
  // Help — question mark in a circle
  help: `<svg aria-hidden="true" viewBox="0 0 16 16"><circle cx="8" cy="8" r="6.25"/><path d="M6.3 6.1a1.75 1.75 0 1 1 2.1 2.05c-.35.1-.4.5-.4.85"/><path d="M8 11.2v.5" stroke-width="1.6"/></svg>`,
  // Retreats — map pin (covers the fall retreat and city trips alike)
  retreat: `<svg aria-hidden="true" viewBox="0 0 16 16"><path d="M8 14.5s-4.5-4.3-4.5-8a4.5 4.5 0 0 1 9 0c0 3.7-4.5 8-4.5 8z"/><circle cx="8" cy="6.5" r="1.7"/></svg>`,
  // Store — shopping bag
  store: `<svg aria-hidden="true" viewBox="0 0 16 16"><path d="M3 5.5h10l-.7 8a1 1 0 0 1-1 .9H4.7a1 1 0 0 1-1-.9z"/><path d="M5.5 5.5V4a2.5 2.5 0 0 1 5 0v1.5"/></svg>`
};

// `group` puts a top-level admin entry under one of the sidebar's small
// labels (NAV_GROUPS). A label is drawn wherever the group changes, so a
// group's entries must sit together; an entry with no group (Dashboard) goes
// above the first label. `footer` entries aren't in the
// list at all -- they're the icon buttons beside the admin's name, for pages
// opened a few times a year. They stay in NAV so filtering and the Help
// assistant's screen names ("Settings -> Notifications") still come from here,
// and a footer entry's children render as tabs on its pages (sectionTabs)
// since the sidebar has nowhere to show them.
//
// `editOnly` marks a nav entry a view-only admin never sees, and it applies at
// both levels: on a top-level item it hides the whole thing, on one child of a
// section it hides just that sub-link. The split matters for Programs --
// view-only staff need Enrollment (the PC/BB roster) but have no business in
// the Registration review queue, which is nothing but approve/waitlist/decline
// buttons. A section whose children are all filtered out disappears entirely,
// and a visible section always lands on its first visible child rather than
// n.id, which would otherwise route a view-only admin straight to a page their
// own nav no longer lists.
export const NAV_GROUPS = { people: 'People', offerings: 'Offerings', money: 'Money' };

export const NAV = {
  parent: [
    { id: 'p-home',      glyph: ICONS.dashboard, label: 'Dashboard' },
    { id: 'p-family',    glyph: ICONS.myfamily,  label: 'My Family' },
    { id: 'p-billing',   glyph: ICONS.wallet,     label: 'Billing' },
    { id: 'p-store',     glyph: ICONS.store,      label: 'Store', showIf: () => isPcFamily() },
    { id: 'p-coaching',  glyph: ICONS.coaching,   label: 'Coaching', showIf: () => (COACHING_MINE.events || []).length > 0 }
  ],
  admin: [
    { id: 'a-home',     glyph: ICONS.dashboard,  label: 'Dashboard' },
    { id: 'a-accounts', glyph: ICONS.family,      label: 'Parents', group: 'people' },
    { id: 'a-students', glyph: ICONS.performers,  label: 'Performers', group: 'people' },
    { id: 'a-reg-applications', glyph: ICONS.registrations, label: 'Programs', group: 'offerings',
      children: [
        { id: 'a-reg-applications', label: 'Registration', editOnly: true },
        { id: 'a-reg-enrollment',   label: 'Enrollment' }
      ] },
    { id: 'a-tapvoice-catalog', glyph: ICONS.classes, label: 'Classes', group: 'offerings',
      children: [
        { id: 'a-tapvoice-catalog', label: 'Catalog' },
        { id: 'a-tapvoice-roster',  label: 'Roster' }
      ] },
    { id: 'a-coaching-events', glyph: ICONS.coaching, label: 'Coaching', group: 'offerings', editOnly: true,
      children: [
        { id: 'a-coaching-events', label: 'Events' },
        { id: 'a-coaching-board',  label: 'Assignment board' },
        { id: 'a-coaching-requests', label: 'Requests' }
      ] },
    { id: 'a-workshops-catalog', glyph: ICONS.summer, label: 'Summer Workshops', group: 'offerings',
      children: [
        { id: 'a-workshops-catalog', label: 'Catalog' },
        { id: 'a-workshops-roster',  label: 'Roster' }
      ] },
    // editOnly: every retreat screen either changes something or exists to
    // decide what to change (send, remind, add, remove).
    { id: 'a-retreats', glyph: ICONS.retreat, label: 'Retreats', group: 'offerings', editOnly: true },
    { id: 'a-store-catalog', glyph: ICONS.store, label: 'Merch Store', group: 'money', editOnly: true,
      children: [
        { id: 'a-store-catalog', label: 'Catalog' },
        { id: 'a-store-orders',  label: 'Orders' }
      ] },
    // Comps stays visible to view-only admins: it is read-only, and it is the
    // page the accountant reads.
    { id: 'a-reports',  glyph: ICONS.billing,     label: 'Billing', group: 'money',
      children: [
        { id: 'a-reports', label: 'Balances & history' },
        { id: 'a-comps',   label: 'Comps' }
      ] },
    { id: 'a-users',    glyph: ICONS.users,       label: 'Users',    footer: true, editOnly: true },
    { id: 'a-settings', glyph: ICONS.settings, label: 'Settings', footer: true, editOnly: true,
      children: [
        { id: 'a-settings',      label: 'General' },
        { id: 'a-birthdays',     label: 'Birthdays' },
        { id: 'a-notifications', label: 'Notifications' }
      ] },
    // editOnly: every guide walks through work a view-only admin cannot do
    // (creating events, approving families, rewording emails). Leaving Help
    // visible to them would offer a section of instructions for buttons they
    // do not have.
    { id: 'a-help',     glyph: ICONS.help,     label: 'Help', footer: true, editOnly: true, also: ['a-help-doc'] }
  ]
};

export function buildNav() {
  const isAdminMode = sessionMode === 'admin';
  const editable = canEdit(session);
  const visibleChildren = n => (n.children || []).filter(c => !c.editOnly || editable);
  const items = isAdminMode
    ? NAV.admin
        .filter(n => !n.editOnly || editable)
        .map(n => n.children ? { ...n, children: visibleChildren(n) } : n)
        .filter(n => !n.children || n.children.length)
    : NAV.parent.filter(n => !n.showIf || n.showIf());

  const modeToggle = isDual(session) && !viewingAsAdmin ? `
    <div style="padding:0 12px 14px">
      <div style="background:rgba(255,255,255,0.05);border-radius:20px;padding:3px;display:grid;grid-template-columns:1fr 1fr;border:1px solid rgba(255,255,255,0.08)">
        <button onclick="switchMode('admin')"  style="padding:5px 0;font-size:10px;border-radius:16px;border:none;background:${sessionMode==='admin'?'var(--gold)':'transparent'};color:${sessionMode==='admin'?'#0e0b16':'var(--muted)'};font-weight:${sessionMode==='admin'?'600':'400'};cursor:pointer;letter-spacing:0.04em;font-family:inherit;text-align:center;width:100%">Admin</button>
        <button onclick="switchMode('parent')" style="padding:5px 0;font-size:10px;border-radius:16px;border:none;background:${sessionMode==='parent'?'var(--gold)':'transparent'};color:${sessionMode==='parent'?'#0e0b16':'var(--muted)'};font-weight:${sessionMode==='parent'?'600':'400'};cursor:pointer;letter-spacing:0.04em;font-family:inherit;text-align:center;width:100%">Parent</button>
      </div>
    </div>` : '';

  document.getElementById('modeToggleWrap').innerHTML = modeToggle;
  const roleLabel = isAdminMode
    ? (session.adminRole === 'view_only' ? 'View-Only Admin' : 'Administrator')
    : 'Parent / Guardian';
  const listed = items.filter(n => !n.footer);
  const footer = items.filter(n => n.footer);
  let group;
  document.getElementById('navMenu').innerHTML = listed.map(n => {
    const label = n.group && n.group !== group ? `<div class="nav-group">${NAV_GROUPS[n.group]}</div>` : '';
    group = n.group;
    if (n.children) {
      const childIds = n.children.map(c => c.id);
      const sectionActive = childIds.includes(currentView);
      const landingId = childIds.includes(n.id) ? n.id : n.children[0].id;
      const parentBtn = `<button id="navsec-${n.id}" class="${sectionActive ? 'section-on' : ''}" onclick="route('${landingId}')"><span class="glyph">${n.glyph}</span>${n.label /* esc-ok: NAV constant */}</button>`;
      const subLinks = sectionActive ? `
        <div class="nav-sub">
          ${n.children.map(c => `<button id="nav-${c.id}" class="nav-sublink ${currentView === c.id ? 'on' : ''}" onclick="route('${c.id}')">${c.label}</button>`).join('')}
        </div>` : '';
      return label + parentBtn + subLinks;
    }
    return label + `<button id="nav-${n.id}" onclick="route('${n.id}')"><span class="glyph">${n.glyph}</span>${n.label /* esc-ok: NAV constant */}</button>`;
  }).join('') + `<div class="nav-drawer-footer"><div class="side-user-row"><div class="side-user-id"><div class="who">${esc(session.firstName + ' ' + session.lastName)}</div><div class="role">${roleLabel}</div></div>${footerButtons(footer)}</div><button class="btn-ghost btn-sm" onclick="logout()">Sign out</button></div>`;
  document.getElementById('sideUtils').innerHTML = footerButtons(footer);

  document.getElementById('userRole').textContent = isAdminMode
    ? (session.adminRole === 'view_only' ? 'View-Only Admin' : 'Administrator')
    : 'Parent / Guardian';
  document.getElementById('brandRole').textContent = isAdminMode ? 'Admin Console' : 'Parent Account';
}
// The icon buttons beside the admin's name. Rendered twice by buildNav -- the
// desktop sidebar footer and the phone drawer's footer -- which is why they
// carry no ids: route()'s highlighting is by id, so these mark themselves.
function footerButtons(entries) {
  if (!entries.length) return '';
  return `<div class="side-utils">${entries.map(n => {
    const ids = [n.id, ...(n.children || []).map(c => c.id), ...(n.also || [])];
    const on = ids.includes(currentView);
    return `<button class="util-btn${on ? ' on' : ''}" onclick="route('${n.id}')" title="${n.label}" aria-label="${n.label}"${on ? ' aria-current="page"' : ''}>${n.glyph}</button>`;
  }).join('')}</div>`;
}

// Tabs across the top of a footer section's pages (Settings: General,
// Birthdays, Notifications) -- the sub-links the sidebar would show if the
// section were in the list.
export function sectionTabs(sectionId) {
  const editable = canEdit(session);
  const section = NAV.admin.find(n => n.id === sectionId);
  const children = (section?.children || []).filter(c => !c.editOnly || editable);
  return `<div class="rt-tabs">${children.map(c =>
    `<button class="${currentView === c.id ? 'on' : ''}" onclick="route('${c.id}')">${c.label}</button>`).join('')}</div>`;
}

export function toggleNav() {
  document.getElementById('navMenu').classList.toggle('open');
  document.getElementById('navBackdrop').classList.toggle('open');
}
export function closeNav() {
  document.getElementById('navMenu').classList.remove('open');
  document.getElementById('navBackdrop').classList.remove('open');
}

export function viewParent(userId) {
  setCurrentParentId(userId);
  route('a-parent-detail');
}
export function viewPerformer(childId) {
  setCurrentPerformerId(childId);
  route('a-performer-detail');
}

let _routePushState = true;
export function route(view, pushState = true) {
  closeNav();
  if (settingsDirty && (currentView === 'a-settings' || currentView === 'a-notifications' || currentView === 'a-birthdays') && view !== currentView) {
    if (!confirm('You have unsaved changes. Leave without saving?')) return;
    setSettingsDirty(false);
  }
  setCurrentView(view);
  let hash = view;
  if (view === 'a-performer-detail' && currentPerformerId) hash += '/' + currentPerformerId;
  else if (view === 'a-parent-detail' && currentParentId) hash += '/' + currentParentId;
  else if (view === 'a-help-doc' && currentHelpSlug) hash += '/' + currentHelpSlug;
  const sub = VIEW_HASH[view] ? VIEW_HASH[view]() : '';
  if (sub) hash += '/' + sub;
  const _state = { view, parentId: currentParentId, performerId: currentPerformerId, helpSlug: currentHelpSlug, sub };
  try {
    if (pushState) history.pushState(_state, '', '#' + hash);
    else history.replaceState(_state, '', '#' + hash);
  } catch (e) { try { location.hash = hash; } catch (_) {} }
  buildNav();  // rebuild so expandable sections open/collapse with the active view
  // Highlight the active nav button, and keep the section parent marked active too
  document.querySelectorAll('.nav button[id^="nav-"]').forEach(b => b.classList.remove('on'));
  const activeBtn = document.getElementById('nav-' + view);
  if (activeBtn) activeBtn.classList.add('on');
  _routePushState = pushState;
  window._routePushState = _routePushState;
  const render = VIEWS[view];
  document.getElementById('main').innerHTML = render ? render() : '';
  if (VIEW_INIT[view]) VIEW_INIT[view]();
  // Keeps the assistant's launcher visibility and "currently viewing" context
  // in step with the new screen. On window rather than imported: assistant.js
  // imports NAV from here, so importing it back would be a cycle.
  window.syncAssistant?.();
  window.scrollTo(0, 0);
  // Background refresh on page navigation
  if (session && isAdmin(session) && view.startsWith('a-')) {
    refreshAdminData();
  } else if (session && view.startsWith('p-')) {
    refreshParentData();
  }
}
let lastRefresh = 0;
export async function refreshAdminData(force = false) {
  const now = Date.now();
  if (!force && now - lastRefresh < 10000) return;
  lastRefresh = now;
  try {
    const [accounts, performers, charges, enrollments, registrations, emailLog, contracts] = await Promise.all([
      apiFetch('/accounts'), apiFetch('/performers'), apiFetch('/charges'),
      apiFetch('/enrollments'), apiFetch('/registrations'), apiFetch('/email-log'), apiFetch('/contracts'),
    ]);
    DB.users = accounts;
    DB.children = performers;
    DB.charges = charges;
    DB.seasonEnrollments = enrollments.map(se => ({
      id: se.id, performerId: se.performerId, programId: se.programId,
      seasonYear: se.seasonYear, status: se.status, billingMode: se.billingMode,
      rateOverride: se.rateOverride ?? null,
      registeredAt: se.createdAt, materialsFeePaid: false, contractSent: false, contractSigned: false
    }));
    DB.registrations = registrations;
    DB.emailLog = emailLog;
    DB.contracts = contracts || [];
    refresh();
  } catch (e) {}
}
let lastParentRefresh = 0;
export async function refreshParentData() {
  const now = Date.now();
  if (now - lastParentRefresh < 10000) return;
  lastParentRefresh = now;
  try {
    const [myAccount, performers, charges, enrollments] = await Promise.all([
      apiFetch('/accounts/' + session.id),
      apiFetch('/accounts/' + session.id + '/performers'),
      apiFetch('/charges?accountId=' + session.id),
      apiFetch('/enrollments'),
    ]);
    DB.users = [myAccount];
    DB.children = performers;
    DB.charges = charges;
    DB.seasonEnrollments = (enrollments || []).map(se => ({
      id: se.id, performerId: se.performerId, programId: se.programId,
      seasonYear: se.seasonYear, status: se.status, billingMode: se.billingMode,
      rateOverride: se.rateOverride ?? null,
      registeredAt: se.createdAt, materialsFeePaid: false, contractSent: false, contractSigned: false
    }));
    refresh();
  } catch (e) {}
}
// Per-view init hooks populate this from their own file (e.g.
// VIEW_INIT['a-settings'] = () => {...} in views/admin-settings.js) — kept
// empty here so core/routing.js never has to import view-specific logic.
export const VIEW_INIT = {};
export function refresh() { route(currentView, false); }

export const VIEWS = {};

// For a view that keeps its own place inside itself (which record is open,
// which tab), so a refresh lands back there rather than on the view's first
// screen. VIEW_HASH[view]() returns the part after "#view/" ('' for none);
// VIEW_RESTORE[view](parts) is handed those parts once, on page load, before
// the view first renders. The three detail views above predate this and keep
// their own ids in state.js.
export const VIEW_HASH = {};
export const VIEW_RESTORE = {};

// Records a move inside a view (opening a record, switching a tab) in the
// hash and in history. push=true makes it a Back/Forward step, the same as
// moving between views; push=false just rewrites the current entry, for a
// re-render that isn't a move (reloading data after a save).
export function updateHash(push = false) {
  const sub = VIEW_HASH[currentView] ? VIEW_HASH[currentView]() : '';
  const hash = currentView + (sub ? '/' + sub : '');
  const state = { ...(history.state || {}), view: currentView, sub };
  try {
    if (push && location.hash !== '#' + hash) history.pushState(state, '', '#' + hash);
    else history.replaceState(state, '', '#' + hash);
  } catch (e) { /* hash is a nicety */ }
}

window.addEventListener('popstate', e => {
  if (!session) return;
  const state = e.state;
  if (!state) return;
  if (state.parentId) setCurrentParentId(state.parentId);
  if (state.performerId) setCurrentPerformerId(state.performerId);
  if (state.helpSlug) setCurrentHelpSlug(state.helpSlug);
  if (VIEW_RESTORE[state.view]) VIEW_RESTORE[state.view]((state.sub || '').split('/').filter(Boolean));
  route(state.view, false);
});

// Cross-cutting "did anything change in an unsaved-form view" tracker — feeds
// route()'s unsaved-changes guard above. Registered once, globally, rather
// than per-view, since it just watches for any input/change bubbling up
// through #main while one of these three views is active.
document.getElementById('main').addEventListener('input', () => {
  if (currentView === 'a-settings' || currentView === 'a-notifications' || currentView === 'a-birthdays') setSettingsDirty(true);
});
document.getElementById('main').addEventListener('change', () => {
  if (currentView === 'a-settings' || currentView === 'a-notifications' || currentView === 'a-birthdays') setSettingsDirty(true);
});

window._routePushState = _routePushState;
Object.assign(window, { toggleNav, closeNav, viewParent, viewPerformer, route });
